import { toast } from '@/components/Toaster/Toast';

import { NativeQSenseControlPlugin } from '@/plugins/NativeQSenseControl';
import { QSenseHubControlPlugin } from '@/plugins/QSenseHubControl';

import { MovementType } from '@/types/MovementTypes';

import { ITagManager } from '../interfaces/ITagManager';

export class TagManager implements ITagManager {
  constructor(private plugin: NativeQSenseControlPlugin | QSenseHubControlPlugin) {}

  /**
   * Updates the user-selected tag for a recording (annotation).
   *
   * This method makes the call to the Native Capacitor plugin, which in turn
   * calls the corresponding method on the native implementation.
   *
   * The method first validates the required parameters, then constructs the
   * annotation ID via `packAnnotation`, and applies the new tag through the
   * shared `TagsState` singleton.
   *
   * @param measurementType - Domain-specific identifier for the type of measurement.
   * @param measurementCount - The count of measurements in the session.
   * @param tagId - The ID of the tag selected by the user.
   *
   * **Edge Cases & Assumptions:**
   * - All parameters are mandatory; missing values result in a rejected call with an explanatory message.
   * - `packAnnotation(count:measurementType:)` performs internal validation on both parameters,
   *   ensuring they fall within acceptable ranges or formats.
   *
   * **Failure Handling:**
   * On missing or invalid parameters, the method calls `call.reject(...)`, causing a rejected promise
   * along with the provided error message.
   */
  async update(measurementType: MovementType, measurementCount: number, tagId: string) {
    try {
      await this.plugin.updateTag({
        MeasurementType: measurementType,
        MeasurementCount: measurementCount,
        TagId: tagId,
      });
    } catch (error) {
      toast({
        title: 'Error updating tag',
        variant: 'error',
        description: (error as Error).message ?? 'An unknown error occurred',
      });
    }
  }

  /**
   * Deletes the tag associated with a specific recording (annotation).
   *
   * This method makes the call to the Native Capacitor plugin, which in turn
   * calls the corresponding method on the native implementation.
   *
   * **Edge Cases & Assumptions:**
   * - Missing parameters trigger rejection to prevent ambiguous deletions.
   *
   * **Failure Handling:**
   * If required parameters are absent or invalid, a rejected promise is returned.
   */
  async delete(measurementType: MovementType, measurementCount: number) {
    try {
      await this.plugin.deleteTag({ MeasurementType: measurementType, MeasurementCount: measurementCount });
    } catch (error) {
      toast({
        title: 'Error deleting tag',
        variant: 'error',
        description: (error as Error).message ?? 'An unknown error occurred',
      });
    }
  }

  /**
   * Clears all tags from every recording.
   *
   * **Failure Handling:**
   * This operation always succeeds (idempotent) and resolves with a success status.
   */
  async reset() {
    await this.plugin.resetTags();
  }
}
